/* eslint-disable react/no-unstable-nested-components */

import { Card, Badge, Button, Table } from "@jhool-io/fe-components";
import { useMemo } from "react";
import { NavigateFunction, useNavigate } from "react-router-dom";
import { ColumnDef } from "@tanstack/react-table";
import { IProviderSuperviseeCaseloadDashboard } from "../../../../../providers/types/providers.types";
import {
    displayNameInRightFormat,
    formatDate,
    hasTimePassed,
} from "../../../../../../utils/helpers";
import ListState from "../../../../../../components/ListState/ListState";
import {
    ISuperviseeTask,
    TaskCategory,
    TaskStatus,
} from "../../../../../tasks/types/tasks.types";
import ChevronRightIcon from "../../../../../../icons/ChevronRight";

const openTaskStatuses = [TaskStatus.NOT_STARTED, TaskStatus.IN_PROGRESS];

function renderSupervisionTaskCell(
    tasks: ISuperviseeTask[] | undefined,
    category: TaskCategory,
    noteType: string,
    navigate: NavigateFunction
) {
    const categoryTasks = tasks?.filter(
        (task) => task.todo_category === category
    );

    if (!categoryTasks?.length) {
        return <span className="text-dark">N/A</span>;
    }

    const openTask = categoryTasks
        .filter((task) => openTaskStatuses.includes(task.status))
        .sort(
            (a, b) =>
                new Date(a.due_date).getTime() - new Date(b.due_date).getTime()
        )[0];

    const lastCompletedAt = categoryTasks
        .filter((task) => task.status === TaskStatus.COMPLETED)
        .map((task) => task.metadata?.completed_at)
        .filter((completedAt): completedAt is string => Boolean(completedAt))
        .sort()
        .pop();

    const lastCompletedLine = lastCompletedAt ? (
        <span className="text-caption text-textSec">
            {`Last completed ${formatDate(`${lastCompletedAt}Z`)}`}
        </span>
    ) : null;

    if (openTask) {
        const isOverdue = hasTimePassed(openTask.due_date);

        return (
            <div className="flex flex-col items-start gap-y-1">
                <Button
                    variant="normal"
                    size="auto"
                    onClick={() =>
                        navigate(
                            `/add-note?session_history_id=${openTask?.metadata?.session_history_id}&show_billable=false&note_type=${noteType}`
                        )
                    }
                >
                    <Badge
                        type={isOverdue ? "danger" : "warning"}
                        content={
                            <span className="flex items-center gap-x-1">
                                Due date
                                {` ${formatDate(openTask.due_date)}`}
                                <ChevronRightIcon
                                    className={
                                        isOverdue
                                            ? "stroke-[--color-danger-text]"
                                            : "stroke-[--color-warning-text]"
                                    }
                                />
                            </span>
                        }
                    />
                </Button>
                {lastCompletedLine}
            </div>
        );
    }

    if (categoryTasks.some((task) => task.status === TaskStatus.COMPLETED)) {
        return (
            <div className="flex flex-col items-start gap-y-1">
                <Badge type="success" content="Done" />
                {lastCompletedLine}
            </div>
        );
    }

    return <span className="text-dark">N/A</span>;
}

interface SuperviseeTasksProps {
    appointments: IProviderSuperviseeCaseloadDashboard[];
    superviseeName: string | null;
}

export default function SuperviseeTasks({
    appointments,
    superviseeName,
}: SuperviseeTasksProps) {
    const navigate = useNavigate();

    const handleRowStyle = () => {
        return {
            backgroundColor: "#FEF8E9",
            borderBottom: "1px solid #ECEEF2",
        };
    };

    const columns: ColumnDef<IProviderSuperviseeCaseloadDashboard>[] =
        useMemo(() => {
            return [
                {
                    accessorKey: "client",
                    header: "CLIENT",
                    cell: ({ row }) => (
                        <span className="text-dark">
                            {displayNameInRightFormat({
                                firstName: row.original.first_name,
                                lastName: row.original.last_name,
                            })}
                        </span>
                    ),
                },
                {
                    accessorKey: "preferred_name",
                    header: "Intake",
                    cell: ({ row }) => {
                        const intakeNoteTask = row.original.tasks?.find(
                            (task) =>
                                task.todo_category ===
                                TaskCategory.WRITE_INTAKE_NOTE
                        );
                        const evaluationTask =
                            row.original.initial_diagnostic_evaluation_task;

                        const evaluationBadge = evaluationTask ? (
                            <Badge
                                type={
                                    evaluationTask.status ===
                                    TaskStatus.COMPLETED
                                        ? "neutral"
                                        : "warning"
                                }
                                content={
                                    evaluationTask.status ===
                                    TaskStatus.COMPLETED
                                        ? "Eval done"
                                        : "Eval pending"
                                }
                            />
                        ) : null;

                        const renderIntakeNoteTask = () => {
                            if (!intakeNoteTask) return null;
                            if (
                                intakeNoteTask.status === TaskStatus.COMPLETED
                            ) {
                                return <Badge content="Done" type="success" />;
                            }
                            return (
                                <Button
                                    variant="normal"
                                    size="auto"
                                    onClick={() =>
                                        navigate(
                                            `/add-note?session_history_id=${intakeNoteTask?.metadata?.session_history_id}&show_billable=true&note_type=individual_intake_note`
                                        )
                                    }
                                >
                                    <Badge
                                        type="danger"
                                        content={
                                            <span className="flex items-center gap-x-1">
                                                Due
                                                <ChevronRightIcon className="stroke-[--color-danger-text]" />
                                            </span>
                                        }
                                    />
                                </Button>
                            );
                        };

                        if (!intakeNoteTask && !evaluationTask) {
                            return <span className="text-dark">N/A</span>;
                        }

                        return (
                            <div className="flex items-center gap-x-2">
                                {renderIntakeNoteTask()}
                                {evaluationBadge}
                            </div>
                        );
                    },
                },
                {
                    accessorKey: "email",
                    header: "SUPERVISION NOTE",
                    cell: ({ row }) =>
                        renderSupervisionTaskCell(
                            row.original.tasks,
                            TaskCategory.WRITE_SUPERVISION_NOTE,
                            "supervision_note",
                            navigate
                        ),
                },
                {
                    accessorKey: "pronoun",
                    header: "QUARTERLY CHECK",
                    cell: ({ row }) =>
                        renderSupervisionTaskCell(
                            row.original.tasks,
                            TaskCategory.WRITE_SUPERVISOR_CHECK_IN_NOTE,
                            "supervisor_check_in",
                            navigate
                        ),
                },
            ];
        }, [navigate]);

    return (
        <>
            {superviseeName ? (
                <h3 className="mb-[--spacing-items] text-title font-medium text-text">{`${superviseeName} Clients`}</h3>
            ) : null}
            <Card classNames="mb-[--spacing-sections] max-w-full overflow-x-auto">
                {appointments.length === 0 ? (
                    <ListState
                        stateHelperText="No supervisee's client list to display"
                        isError={false}
                        emptyMessage="No supervisee's client"
                        context="general"
                    />
                ) : (
                    <Table
                        columns={columns}
                        data={appointments}
                        handleRowStyle={handleRowStyle}
                    />
                )}
            </Card>
        </>
    );
}
